/* Content → Vaults: the vaults attached to one library section, shown as
   folder cards (Vaults Section handoff, option 1b). Empty → one big "Add vaults"
   target; attached → a grid of folder cards with a media mosaic, analysis
   progress and Open / Analyse / ×, ending in an "Add vaults" card. Run status is
   server-owned (content_classification_runs) and polled while any run is in
   flight, so "Analysing" survives a reload. */

import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { ContentLibrarySection, OfVault, OnDemandVault, VaultAnalysisStatus, VaultContentItem } from '../../../../api/client';
import { fmtDuration, isVideoItem } from '../../SalesLadder/MediaBadge';
import { VaultThumb } from '../../Sales/VaultThumb';
import { Btn, ErrorLine, Icon, InfoLine, Loading, Pill, RunTag, Spin } from '../kit';
import { font } from '../style';
import { C } from '../tokens';
import { VaultViewer } from './VaultViewer';

const POLL_MS = 4000;
/** Mosaic tiles per folder card: one tall tile + two stacked. */
const MOSAIC = 3;

type Tile = Pick<VaultContentItem, 'type' | 'thumb_url' | 'duration'>;
type Preview = { items: Tile[]; more: boolean };

/** Run `fn` over `ids` with a few in flight at once — each call is a live OnlyFans sweep. */
async function pool(ids: string[], size: number, fn: (id: string) => Promise<void>) {
  const queue = [...ids];
  const worker = async () => {
    for (;;) {
      const id = queue.shift();
      if (id === undefined) return;
      await fn(id);
    }
  };
  await Promise.all(Array.from({ length: Math.min(size, queue.length) }, worker));
}

export function VaultsSection({ accountId, section, info, onVaults }: { accountId: string; section: ContentLibrarySection; info?: string; onVaults?: (n: number) => void }) {
  const { t } = useTranslation('assistantCompact');
  const [vaults, setVaults] = useState<OnDemandVault[]>([]);
  const [all, setAll] = useState<OfVault[]>([]);
  const [counted, setCounted] = useState<Set<string>>(new Set());
  /** Vaults whose item total is known — from the covers call, before the slow counts. */
  const [sized, setSized] = useState<Set<string>>(new Set());
  const [previews, setPreviews] = useState<Record<string, Preview>>({});
  const [statuses, setStatuses] = useState<Record<string, VaultAnalysisStatus>>({});
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [picker, setPicker] = useState(false);
  const [view, setView] = useState<OnDemandVault | null>(null);
  const [ask, setAsk] = useState<{ v: OnDemandVault; eligible: number } | null>(null);

  const loadCounts = useCallback((ids: string[]) => pool(ids, 3, async (id) => {
    try {
      const c = await api.getVaultCounts(accountId, id);
      // The sweep stops at 5000 items; OF's own vault total (from covers) can be larger.
      setVaults((p) => p.map((v) => (v.id === id ? { ...v, total_items: Math.max(c.total_items, v.total_items), classified_items: c.classified_items, needs_review_items: c.needs_review_items } : v)));
      setSized((s) => new Set(s).add(id));
    } catch { /* keep last value */ } finally {
      setCounted((s) => new Set(s).add(id));
    }
  }), [accountId]);

  // Fallback mosaic (Fansly, or a vault OF sent no covers for): the first page of media.
  const loadPreviews = useCallback((ids: string[]) => pool(ids, 2, async (id) => {
    try {
      const page = await api.getVaultContent(accountId, id, 0, MOSAIC + 1);
      setPreviews((p) => ({ ...p, [id]: { items: page.items.slice(0, MOSAIC), more: page.has_more || page.items.length > MOSAIC } }));
    } catch {
      setPreviews((p) => ({ ...p, [id]: p[id] ?? { items: [], more: false } }));
    }
  }), [accountId]);

  // One vault-list read gives every card its total and mosaic; the per-vault counts
  // (a full live sweep each, only needed for analysed / review numbers) come last.
  const loadDetails = useCallback(async (ids: string[]) => {
    let covered = new Set<string>();
    try {
      const covers = await api.getVaultCovers(accountId, ids);
      const byId = new Map(covers.map((c) => [c.vault_id, c]));
      setVaults((p) => p.map((v) => { const c = byId.get(v.id); return c ? { ...v, total_items: c.total_items } : v; }));
      setSized((s) => { const n = new Set(s); byId.forEach((_, id) => n.add(id)); return n; });
      setPreviews((p) => {
        const n = { ...p };
        byId.forEach((c, id) => {
          if (c.total_items > 0 && !c.medias.length) return;
          n[id] = { items: c.medias.slice(0, MOSAIC).map((m) => ({ type: m.type, thumb_url: m.thumb_url, duration: null })), more: c.total_items > MOSAIC };
        });
        return n;
      });
      covered = new Set(covers.filter((c) => c.medias.length || !c.total_items).map((c) => c.vault_id));
    } catch { /* fall back to the per-vault first page below */ }
    await loadPreviews(ids.filter((id) => !covered.has(id)));
    await loadCounts(ids);
  }, [accountId, loadCounts, loadPreviews]);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const o = await api.getContentLibraryOverview(accountId, section);
      setVaults(o.vaults);
      setAll(o.all_vaults);
      setCredits(o.remaining_generations);
      setStatuses(Object.fromEntries(o.statuses.map((s) => [s.vault_id, s])));
      setCounted(new Set());
      setSized(new Set());
      setPreviews({});
      void loadDetails(o.vaults.map((v) => v.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('content.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [accountId, section, loadDetails, t]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => { onVaults?.(vaults.length); }, [vaults.length, onVaults]);

  const running = useMemo(() => Object.values(statuses).some((s) => s.running), [statuses]);
  useEffect(() => {
    if (!running) return;
    let off = false;
    const h = window.setInterval(async () => {
      try {
        const r = await api.getAnalysisStatus(accountId);
        if (off) return;
        setStatuses(Object.fromEntries(r.vaults.map((s) => [s.vault_id, s])));
        setCredits(r.remaining_generations);
        if (!r.vaults.some((s) => s.running)) void loadCounts(vaults.map((v) => v.id));
      } catch { /* try again next tick */ }
    }, POLL_MS);
    return () => { off = true; window.clearInterval(h); };
  }, [running, accountId, loadCounts, vaults]);

  const start = async (v: OnDemandVault) => {
    setAsk(null);
    setErr(null);
    setStatuses((s) => ({ ...s, [v.id]: { vault_id: v.id, status: 'running', running: true, tagged: v.classified_items, total: v.total_items } }));
    try {
      const st = await api.analyseVault(accountId, v.id);
      setStatuses((s) => ({ ...s, [v.id]: st }));
      if (!st.running) void loadCounts([v.id]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('content.analyseFailed'));
      setStatuses((s) => { const n = { ...s }; delete n[v.id]; return n; });
    }
  };
  const analyse = (v: OnDemandVault) => {
    const eligible = Math.max(0, v.total_items - v.classified_items);
    // Straight in when credits cover it; ask first when they don't.
    if (credits !== null && (credits <= 0 || eligible > credits)) setAsk({ v, eligible });
    else void start(v);
  };
  const stop = async (v: OnDemandVault) => {
    setStatuses((s) => (s[v.id] ? { ...s, [v.id]: { ...s[v.id]!, cancel_requested: true } } : s));
    try {
      const st = await api.stopVaultAnalysis(accountId, v.id);
      setStatuses((s) => ({ ...s, [v.id]: st }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('content.stopFailed'));
    }
  };
  const remove = async (v: OnDemandVault) => {
    const prev = vaults;
    setVaults(vaults.filter((x) => x.id !== v.id));
    try {
      await api.detachContentLibraryVault(accountId, section, v.id);
    } catch (e) {
      setVaults(prev);
      setErr(e instanceof Error ? e.message : t('content.detachFailed'));
    }
  };
  const add = async (ids: string[]) => {
    setErr(null);
    try {
      for (const id of ids) await api.attachContentLibraryVault(accountId, section, id);
      const fresh = await api.listContentLibraryVaults(accountId, section);
      // Keep the totals already loaded for the cards that were there before.
      setVaults((p) => fresh.map((v) => p.find((o) => o.id === v.id) ?? v));
      setPicker(false);
      void loadDetails(ids);
    } catch (e) {
      // Leave the picker open so the operator can retry; the error shows inside it.
      throw e instanceof Error ? e : new Error(t('content.attachFailed'));
    }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const fresh = await api.syncAccountVaults(accountId);
      setAll(fresh);
      // Pick up renames and new media on the attached cards too.
      const byId = new Map(fresh.map((v) => [v.id, v]));
      setVaults((p) => p.map((v) => ({ ...v, vault_name: byId.get(v.id)?.vault_name ?? v.vault_name })));
      void loadDetails(vaults.map((v) => v.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('content.loadFailed'));
    } finally {
      setSyncing(false);
    }
  };

  const attached = new Set(vaults.map((v) => v.id));
  const options = all.filter((v) => !attached.has(v.id));
  const items = vaults.reduce((n, v) => n + v.total_items, 0);
  const allCounted = vaults.every((v) => sized.has(v.id));
  const countLabel = !vaults.length
    ? t('content.noneAttached')
    : allCounted
      ? `${t('content.nAttached', { count: vaults.length })} · ${t('content.nItems', { count: items })}`
      : t('content.nAttached', { count: vaults.length });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: font(600, 13), color: C.text }}>{t('content.vaults')}</span>
        <span style={{ font: font(400, 12), color: C.muted }}>{countLabel}</span>
        <span style={{ flex: 1 }} />
        {credits !== null && <Pill>{t('content.credits', { count: credits })}</Pill>}
        <Btn kind="ghost" icon={syncing ? undefined : 'refresh-cw'} onClick={() => void sync()} disabled={syncing} title={t('content.refreshTip')}>{syncing ? <Spin /> : null}{t('content.refresh')}</Btn>
      </div>
      {err && <ErrorLine>{err}</ErrorLine>}
      {loading ? <Loading /> : !vaults.length ? (
        <EmptyAdd onClick={() => setPicker(true)} />
      ) : (
        <>
          {info && <InfoLine>{info}</InfoLine>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16, paddingTop: 4 }}>
            {vaults.map((v) => (
              <FolderCard
                key={v.id}
                accountId={accountId}
                vault={v}
                status={statuses[v.id]}
                known={counted.has(v.id)}
                sized={sized.has(v.id)}
                preview={previews[v.id]}
                onOpen={() => setView(v)}
                onAnalyse={() => analyse(v)}
                onStop={() => void stop(v)}
                onRemove={() => void remove(v)}
              />
            ))}
            {options.length > 0 && (
              <button type="button" onClick={() => setPicker(true)} className="cpt-hover-soft cpt-hover-line" style={{ marginTop: 10, minHeight: 250, background: 'transparent', border: `1.5px dashed ${C.borderHi}`, borderRadius: 10, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: C.purplePale }}>
                <span style={{ width: 44, height: 44, borderRadius: 10, background: C.tint16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={22} /></span>
                <span style={{ font: font(600, 14) }}>{t('content.addVaults')}</span>
              </button>
            )}
          </div>
        </>
      )}
      {picker && (
        <VaultPicker
          options={options}
          anyAttached={vaults.length > 0}
          syncing={syncing}
          onSync={() => void sync()}
          onAdd={add}
          onClose={() => setPicker(false)}
        />
      )}
      {ask && (
        <Confirm
          title={ask.v.vault_name || ask.v.id}
          body={credits !== null && credits <= 0 ? t('content.noCredits') : t('content.shortCredits', { count: ask.eligible, credits: credits ?? 0 })}
          canGo={(credits ?? 0) > 0}
          goLabel={t('content.analyseAnyway')}
          onGo={() => void start(ask.v)}
          onClose={() => setAsk(null)}
        />
      )}
      {view && (
        <VaultViewer
          accountId={accountId}
          vault={view}
          status={statuses[view.id]}
          onClose={() => setView(null)}
          onChanged={() => void loadCounts([view.id])}
          onCredits={(n) => setCredits(n)}
        />
      )}
    </div>
  );
}

/* ── Empty state: one big target with three folder shapes ───────── */

function FolderShape({ w, h, tab, bg, tabBg, children }: { w: number; h: number; tab: number; bg: string; tabBg: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <div style={{ width: tab, height: Math.round(h / 6.5), background: tabBg, borderRadius: '4px 4px 0 0' }} />
      <div style={{ width: w, height: h, background: bg, borderRadius: '0 6px 6px 6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.purplePale }}>{children}</div>
    </div>
  );
}

function EmptyAdd({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <button type="button" onClick={onClick} className="cpt-hover-soft cpt-hover-line" style={{ height: 220, width: '100%', background: 'transparent', border: `1.5px dashed ${C.borderHi}`, borderRadius: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <FolderShape w={52} h={38} tab={22} bg={C.track} tabBg={C.track} />
        <FolderShape w={64} h={46} tab={26} bg={C.tint20} tabBg={C.tintLine}><Icon name="plus" size={22} /></FolderShape>
        <FolderShape w={52} h={38} tab={22} bg={C.track} tabBg={C.track} />
      </div>
      <span style={{ font: font(600, 17), color: C.text }}>{t('content.addVaults')}</span>
      <span style={{ font: font(400, 13, 1.45), color: C.muted, maxWidth: 420, textAlign: 'center', textWrap: 'pretty' } as CSSProperties}>{t('content.emptyDesc')}</span>
    </button>
  );
}

/* ── Folder card ────────────────────────────────────────────────── */

function FolderCard({ accountId, vault: v, status: st, known, sized, preview, onOpen, onAnalyse, onStop, onRemove }: {
  accountId: string; vault: OnDemandVault; status?: VaultAnalysisStatus; known: boolean; sized: boolean; preview?: Preview;
  onOpen: () => void; onAnalyse: () => void; onStop: () => void; onRemove: () => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const run = !!st?.running;
  const stopping = run && !!st?.cancel_requested;
  const tot = run && st.total ? st.total : v.total_items;
  const an = run ? Math.max(st.tagged, 0) : v.classified_items;
  const done = known && tot > 0 && an >= tot;
  const empty = (known || sized) && v.total_items === 0;
  const w = tot ? Math.round((Math.min(an, tot) / tot) * 100) : 0;
  const tiles = preview?.items ?? [];
  const more = Math.max(0, v.total_items - (MOSAIC - 1));
  const name = v.vault_name || v.id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ width: 84, height: 10, background: C.card, border: `1px solid ${C.border}`, borderBottom: 0, borderRadius: '7px 7px 0 0' }} />
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '0 10px 10px 10px', marginTop: -1, padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div onClick={onOpen} style={{ height: 104, display: 'grid', gridTemplateColumns: '2fr 1fr', gridTemplateRows: '1fr 1fr', gap: 3, borderRadius: 7, overflow: 'hidden', cursor: 'pointer' }}>
          {empty ? (
            <div style={{ gridColumn: '1 / -1', gridRow: '1 / -1', border: `1px dashed ${C.borderHi}`, borderRadius: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, color: C.faint, font: font(400, 12) }}>
              <Icon name="folder" size={20} />{t('content.emptyFolder')}
            </div>
          ) : (
            Array.from({ length: MOSAIC }, (_, k) => {
              const it = tiles[k];
              const isMore = k === MOSAIC - 1 && (preview?.more || v.total_items > MOSAIC);
              const vid = it && isVideoItem(it.type);
              return (
                <div key={k} style={{ gridRow: k === 0 ? '1 / 3' : 'auto', position: 'relative', overflow: 'hidden', background: C.stripe }}>
                  {it && <VaultThumb accountId={accountId} url={it.thumb_url} className="w-full h-full" />}
                  {!preview && k === 0 && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}><Spin /></span>}
                  {vid && fmtDuration(it.duration) && (
                    <span style={{ position: 'absolute', left: 5, bottom: 5, display: 'flex', alignItems: 'center', gap: 3, padding: '1px 5px', borderRadius: 4, background: 'color-mix(in srgb, var(--om-page) 60%, transparent)', font: font(500, 10), color: C.text }}>
                      <Icon name="play" size={8} />{fmtDuration(it.duration)}
                    </span>
                  )}
                  {isMore && more > 0 && (
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'color-mix(in srgb, var(--om-page) 60%, transparent)', font: font(600, 13), color: C.text }}>+{more}</span>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span title={name} style={{ font: font(600, 14), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: font(400, 12), color: empty ? C.faint : C.muted, minHeight: 16 }}>
            {!known && !sized ? <Spin /> : empty ? t('content.noMedia') : t('content.nItems', { count: v.total_items })}
            {!run && v.needs_review_items > 0 && <span style={{ color: C.purplePale, whiteSpace: 'nowrap' }}>· {t('content.review', { count: v.needs_review_items })}</span>}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {run ? <RunTag>{stopping ? t('content.stopping') : t('content.analysing')}</RunTag> : null}
          <div style={{ flex: 1, height: 4, borderRadius: 99, background: C.track, overflow: 'hidden' }}>
            <div style={{ height: 4, width: `${w}%`, background: run ? C.purpleLt : C.purple, transition: 'width .25s linear' }} />
          </div>
          <span style={{ font: font(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{known || run ? (tot ? t('content.nAnalysed', { n: an, total: tot }) : '—') : null}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn kind="ghost" height={30} onClick={onOpen} style={{ flex: 1, justifyContent: 'center', font: font(500, 12.5) }}>{t('content.open')}</Btn>
          {run ? (
            <Btn kind="ghost" height={30} disabled={stopping} onClick={onStop} title={t('content.stopTip')} style={cardAction}>{stopping ? t('content.stopping') : t('content.stop')}</Btn>
          ) : (
            <Btn kind="ghost" height={30} disabled={done || !known || empty} onClick={onAnalyse} title={done ? t('content.allAnalysedTip') : t('content.analyseTip')} style={cardAction}>{done ? t('content.analysed') : t('content.analyse')}</Btn>
          )}
          <button type="button" onClick={onRemove} disabled={run} title={t('content.removeVault')} className="cpt-hover-text cpt-hover-border" style={{ width: 30, height: 30, flex: 'none', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 6, color: C.faint, cursor: run ? 'default' : 'pointer', opacity: run ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="x" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

const cardAction: CSSProperties = { flex: 1, justifyContent: 'center', border: `1px solid ${C.tintLine}`, color: C.purplePale, font: font(500, 12.5) };

/* ── Picker: search + checkboxes, opened from either add target ─── */

function VaultPicker({ options, anyAttached, syncing, onSync, onAdd, onClose }: {
  options: OfVault[]; anyAttached: boolean; syncing: boolean; onSync: () => void; onAdd: (ids: string[]) => Promise<void>; onClose: () => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const needle = q.trim().toLowerCase();
  const shown = options.filter((o) => !needle || (o.vault_name || o.id).toLowerCase().includes(needle));
  const n = picked.length;

  const go = async () => {
    if (!n || adding) return;
    setAdding(true);
    setErr(null);
    try {
      await onAdd(picked);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('content.attachFailed'));
      setAdding(false);
    }
  };

  return (
    <div className="cpt-root" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: '100%', maxHeight: '80vh', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 12, boxShadow: '0 20px 50px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 12px 18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
            <span style={{ font: font(600, 16), color: C.text }}>{t('content.addVaults')}</span>
            <span style={{ font: font(400, 12.5), color: C.muted }}>{t('content.pickDesc')}</span>
          </div>
          <button type="button" onClick={onSync} disabled={syncing} title={t('content.refreshTip')} className="cpt-hover-icon" style={iconBtn}>{syncing ? <Spin size={15} /> : <Icon name="refresh-cw" size={15} />}</button>
          <button type="button" onClick={onClose} title={t('close')} className="cpt-hover-icon" style={iconBtn}><Icon name="x" size={17} /></button>
        </div>
        <div style={{ padding: '0 16px 10px' }}>
          <div className="cpt-message-search" style={{ height: 34, display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7 }}>
            <Icon name="search" size={14} color={C.faint} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('content.searchVaults')} style={{ flex: 1, minWidth: 0, background: 'transparent', border: 0, outline: 'none', color: C.text, font: font(400, 13) }} />
          </div>
        </div>
        <div className="cpt-thin-scroll" style={{ flex: 1, overflow: 'auto', padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {shown.map((o) => {
            const on = picked.includes(o.id);
            return (
              <button key={o.id} type="button" className="cpt-hover-row" onClick={() => setPicked((p) => (on ? p.filter((x) => x !== o.id) : [...p, o.id]))} style={{ height: 44, flex: 'none', padding: '0 10px', border: 0, borderRadius: 8, background: on ? C.tint16 : 'transparent', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ width: 18, height: 18, flex: 'none', boxSizing: 'border-box', borderRadius: 5, border: `1px solid ${on ? C.purple : 'var(--om-border-strong)'}`, background: on ? C.purple : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{on && <Icon name="check" size={12} />}</span>
                <Icon name="folder" size={18} color={C.purplePale} />
                <span style={{ flex: 1, minWidth: 0, font: font(500, 13.5), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.vault_name || o.id}</span>
              </button>
            );
          })}
          {!shown.length && <div style={{ padding: 24, textAlign: 'center', font: font(400, 13), color: C.faint }}>{options.length ? t('content.noMatch') : anyAttached ? t('content.allAdded') : t('content.noneOnAccount')}</div>}
        </div>
        {err && <div style={{ padding: '0 16px 10px' }}><ErrorLine>{err}</ErrorLine></div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: `1px solid ${C.divider}` }}>
          <span style={{ flex: 1, font: font(400, 12.5), color: C.muted }}>{n ? t('content.nSelected', { count: n }) : t('content.nAvailable', { count: options.length })}</span>
          <Btn kind="text" height={34} onClick={onClose} style={{ color: C.sub, font: font(500, 13) }}>{t('cancel')}</Btn>
          <Btn kind="primary" height={34} icon={adding ? undefined : 'plus'} disabled={!n || adding} onClick={() => void go()} style={{ padding: '0 16px', font: font(600, 13) }}>{adding ? <Spin /> : null}{n ? t('content.addN', { count: n }) : t('content.add')}</Btn>
        </div>
      </div>
    </div>
  );
}

const iconBtn: CSSProperties = { width: 30, height: 30, flex: 'none', background: 'transparent', border: 0, borderRadius: 6, color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

export function Confirm({ title, body, canGo, goLabel, onGo, onClose }: { title: string; body: string; canGo: boolean; goLabel: string; onGo: () => void; onClose: () => void }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <div className="cpt-root" style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 400, background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, boxShadow: '0 24px 64px rgba(0,0,0,.5)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span style={{ font: font(600, 14), color: C.text }}>{t('content.confirmTitle', { name: title })}</span>
        <span style={{ display: 'flex', gap: 8, font: font(400, 12.5, 1.5), color: C.body }}><Icon name="alert-circle" size={15} color={C.purplePale} />{body}</span>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn kind="ghost" onClick={onClose}>{canGo ? t('cancel') : t('close')}</Btn>
          {canGo && <Btn kind="primary" onClick={onGo}>{goLabel}</Btn>}
        </div>
      </div>
    </div>
  );
}
